import React, {useMemo} from 'react';
import {Col, Grid, Row} from "xps-react";
import {
    AgeInput,
    BirthdateInput,
    Button,
    GenderInput,
    MiddleInitial,
    Name,
    PersonalPlanningHorizonInput,
    StateOfResidencyInput,
} from "../../components";
import {ReferenceDataType} from "../../models/referenceData/ReferenceDataType";
import {
    calculateAgeFromBirthdate,
    calculateBirthdateFromAge,
    personalPlanningHorizonFromAge,
    toDisplayDateFormat,
    toISO
} from "../../utils/dateUtils";
import {useHistory, useParams} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {
    clearPartnerWorkstationState,
    selectLinkedClientName,
    selectLinkedUlek
} from "../PartnerWorkstation/partnerWorkstationSlice";
import {
    selectIsSaveButtonClicked,
    selectNewClientFormInteractions,
    selectNewClientFormValidation, selectNewClientFormValidationForSecondary,
    selectProfileRequestForm, selectProfileRequestFormDataForSecondary,
    setNewClientFormInteractions,
    setProfileRequestForm, setProfileRequestFormDataForSecondary
} from "./NewClientProfileSlice";
import {validateInputRemovingSpaces} from "../../utils/stringUtils";
import {RouteWithProfileType} from "../../routes/types";
import useProfileEditableState from "../../hooks/useProfileEditableState";
import {selectReleaseToggles} from "../../ReleaseToggles/releaseTogglesSlice";
import { ProfileRequest } from '../models/ProfileResponse';

type PrimaryContactProps = {
    referenceData: ReferenceDataType,
    isFromOne: boolean,
    isPrimaryMember?: boolean,
    showDataForClientRelationshipGroup?:boolean,
    onValidateOnBlur?: () => void,
}

export const NewClientPrimaryContact = ({
    referenceData,
    isFromOne,
    isPrimaryMember = true,
    showDataForClientRelationshipGroup = false,
    onValidateOnBlur,
}: PrimaryContactProps) => {
    const dispatch = useAppDispatch();
    const linkedUlek = useAppSelector(selectLinkedUlek);
    const linkedClientName = useAppSelector(selectLinkedClientName);

    const profileRequestFormForPrimary = useAppSelector(selectProfileRequestForm);

    const profileRequestFormForSecondary = useAppSelector(selectProfileRequestFormDataForSecondary);

    let profileRequestForm: ProfileRequest;
    if (isPrimaryMember) {
        profileRequestForm = profileRequestFormForPrimary!;
    } else {
        profileRequestForm = profileRequestFormForSecondary!;
    }
    const {newClientProfileForm} = useAppSelector(selectNewClientFormValidation);
    const {newClientProfileFormForSecondary} = useAppSelector(selectNewClientFormValidationForSecondary);
    const formInteractions = useAppSelector(selectNewClientFormInteractions);
    const isSaveButtonClicked = useAppSelector(selectIsSaveButtonClicked);
    const history = useHistory();
    const listOfStates = Object.entries(referenceData.statesOfResidency).map(([, value]) => value).sort();
    const {type} = useParams<RouteWithProfileType>();
    const {isProfileWithProposalsOrArchived} = useProfileEditableState();
    const releaseToggles: any = useAppSelector(selectReleaseToggles);

    const updatePrimaryContactHandler = (e: any) => dispatch(setProfileRequestForm({
        ...profileRequestForm,
        primaryContact: {
            ...profileRequestForm.primaryContact,
            [e.target.name]: validateInputRemovingSpaces(e.target.value)
        }
    }));

    const updateSecondaryContactHandler = (e: any) => dispatch(setProfileRequestFormDataForSecondary({
        ...profileRequestForm,
        primaryContact: {
            ...profileRequestForm.primaryContact,
            [e.target.name]: validateInputRemovingSpaces(e.target.value)
        }
    }));

    const handleLinkExistingClient = () => {
        history.push(`/PartnerWorkstation`)
    }

    const hasLinkedClient = (): boolean => {
        return linkedUlek !== '';
    }

    const handleRemoveLink = () => {
        dispatch(clearPartnerWorkstationState());
    }

    const profileTypeFormattedText = () => {
        switch (type.toUpperCase()) {
            case ('PROSPECT'):
                return {
                    header: 'Prospect',
                    isLinkButtonEnabled: false,
                }
            default:
                return {
                    header: 'Client',
                    isLinkButtonEnabled: true,
                }
        }
    }

    const preferredNameLabel = useMemo(() => {
        return releaseToggles?.enableOneIntegration && type.toUpperCase()=='CLIENT'
            ? "Preferred Name"
            : "First Name";
    }, [releaseToggles?.enableOneIntegration, type]);

    return (
        <Grid>
            <Row>
                <Col>
                    {showDataForClientRelationshipGroup === false ? <div className="h4 sections primaryContactLabel">Primary Contact Information</div> :
                        isPrimaryMember && <div className="h4 sections primaryContactLabel planning-group-label">Client Relationship Group Member Information</div>}
                    <hr className="section"/>
                    {type && type.toUpperCase() !== 'PROSPECT' && type.toUpperCase() !== 'SAMPLE' && <div className="linked-partner">
                        {showDataForClientRelationshipGroup === false ? <label
                            className="h5 linked-partner-label">{`Linked Partner Workstation ${profileTypeFormattedText().header}`}</label> :  <label
                            className="h5 linked-partner-label-planning-group">Linked Partner Workstation Client</label>}
                        {showDataForClientRelationshipGroup === false ? <div className={"linked-partner-content"} aria-label="linkedPartner">
                            {hasLinkedClient() &&
                                <div className={"full-name-and-button remove-button"} aria-label="linkedPartnerName">
                                    {linkedClientName}
                                    <Button
                                        kind="borderless"
                                        onClick={handleRemoveLink}
                                    >
                                        Remove Link
                                    </Button>
                                </div>
                                ||
                                <div className={"full-name-and-button"}>
                                    <span><i>{`No ${profileTypeFormattedText().header.toLowerCase()} linked.`}</i></span>
                                    {profileTypeFormattedText().isLinkButtonEnabled ?
                                        <Button
                                            icon="left"
                                            iconName="link"
                                            kind="borderless"
                                            onClick={handleLinkExistingClient}
                                            disabled={isProfileWithProposalsOrArchived}
                                        >
                                            Link Existing Client
                                        </Button> : null
                                    }
                                </div>
                            }
                        </div> :    <div className="detail input-wrapper input-inline-align--right planning-group-fields-lpwc">
                            {profileRequestForm.primaryContact.firstName}  {profileRequestForm.primaryContact.lastName}
                        </div>}
                    </div>}
                    {isPrimaryMember === true && <Name name={profileRequestForm.primaryContact.firstName}
                          className="first-name"
                          label={preferredNameLabel}
                          inputName="firstName"
                          inputId="firstNameInput"
                          onChange={updatePrimaryContactHandler}
                          required={true}
                          error={newClientProfileForm?.firstName}
                          hasInteractions={formInteractions.firstName}
                          whenUserHasInteracted={() => {
                              dispatch(setNewClientFormInteractions({
                                  ...formInteractions,
                                  firstName: true
                              }));
                              onValidateOnBlur?.();
                          }}
                          forceShowErrors={isSaveButtonClicked}
                          maxLength={25}/>}
                    {isPrimaryMember === false && <Name name={profileRequestForm.primaryContact.firstName}
                                                       className="first-name"
                                                       label={preferredNameLabel}
                                                       inputName="firstName"
                                                       inputId="firstNameInput"
                                                       onChange={updateSecondaryContactHandler}
                                                       required={true}
                                                       error={newClientProfileFormForSecondary?.firstName}
                                                       hasInteractions={formInteractions.firstName}
                                                       whenUserHasInteracted={() => {
                                                           dispatch(setNewClientFormInteractions({
                                                               ...formInteractions,
                                                               firstName: true
                                                           }));
                                                           onValidateOnBlur?.();
                                                       }}
                                                       forceShowErrors={isSaveButtonClicked}
                                                       maxLength={25}/>}
                    {showDataForClientRelationshipGroup === false ? <MiddleInitial name={profileRequestForm.primaryContact.middleInitial}
                                   className="middle-initial"
                                   label={"Middle Initial"}
                                   maxLength={1}
                                   required={false}
                                   onChange={updatePrimaryContactHandler}/> :  profileRequestForm.primaryContact.middleInitial !== "" && <div className="parent">
                        <label className="planning-group-label">Middle Initial</label>
                        <div className="detail input-wrapper input-inline-align--right planning-group-fields">
                            {profileRequestForm.primaryContact.middleInitial}
                        </div>
                    </div>}
                    {showDataForClientRelationshipGroup === false ? <Name name={profileRequestForm.primaryContact.lastName}
                          className="last-name"
                          label={"Last Name"}
                          onChange={updatePrimaryContactHandler}
                          required={true}
                          error={newClientProfileForm?.lastName}
                          hasInteractions={formInteractions.lastName}
                          whenUserHasInteracted={() => {
                              dispatch(setNewClientFormInteractions({
                                  ...formInteractions,
                                  lastName: true
                              }));
                              onValidateOnBlur?.();
                          }}
                          forceShowErrors={isSaveButtonClicked}
                          maxLength={25}
                          readOnly={isFromOne && !!profileRequestForm.primaryContact.lastName}/> : <div className="parent">
                        <label className="planning-group-label">Last Name</label>
                        <div className="detail input-wrapper input-inline-align--right planning-group-fields">
                            {profileRequestForm.primaryContact.lastName}
                        </div>
                    </div>}

                    {showDataForClientRelationshipGroup === false  ? <BirthdateInput birthdate={profileRequestForm.primaryContact.birthdate}
                                    onChange={(birthdate: string) => dispatch(setProfileRequestForm({
                                        ...profileRequestForm,
                                        primaryContact: {
                                            ...profileRequestForm.primaryContact,
                                            birthdate: toISO(birthdate),
                                            age: calculateAgeFromBirthdate(birthdate)
                                        }
                                    }))}
                                    width={'300px'}
                                    disabled={isFromOne && !!profileRequestForm.primaryContact.birthdate}
                    /> : <div className="parent">
                        <label className="planning-group-label">Birthdate</label>
                        <div className="detail input-wrapper input-inline-align--right planning-group-fields">
                            {toDisplayDateFormat(profileRequestForm.primaryContact.birthdate)}
                        </div>
                    </div>}

                    {showDataForClientRelationshipGroup === false ? <AgeInput age={profileRequestForm.primaryContact.age || 0}
                              className="age"
                              inputDescription="Entering birthdate will calculate age"
                              onChange={
                                  (age: number) =>
                                      dispatch(setProfileRequestForm({
                                          ...profileRequestForm,
                                          primaryContact: {
                                              ...profileRequestForm.primaryContact,
                                              age: age,
                                              birthdate: calculateBirthdateFromAge(age),
                                              personalPlanningHorizon: personalPlanningHorizonFromAge(age,
                                                  profileRequestForm.primaryContact.personalPlanningHorizon)
                                          }
                                      }))
                              }
                              error={newClientProfileForm?.age}
                              hasInteractions={formInteractions.age}
                              whenUserHasInteracted={() => {
                                  dispatch(setNewClientFormInteractions({
                                      ...formInteractions,
                                      age: true
                                  }));
                                  onValidateOnBlur?.();
                              }}
                              forceShowErrors={isSaveButtonClicked}
                              readOnly={isFromOne && !!profileRequestForm.primaryContact.birthdate}/> : <div className="parent">
                        <label className="planning-group-label">Age</label>
                        <div className="detail input-wrapper input-inline-align--right planning-group-fields">
                            {profileRequestForm.primaryContact.age}
                        </div>
                    </div>}
                    {showDataForClientRelationshipGroup === false ? <GenderInput
                        value={profileRequestForm.primaryContact.gender}
                        onChange={(e: any) => dispatch(setProfileRequestForm({
                            ...profileRequestForm,
                            primaryContact: {
                                ...profileRequestForm.primaryContact,
                                gender: e.itemText
                            }
                        }))}
                        disabled={isFromOne && !!profileRequestForm.primaryContact.gender}
                    /> :  <div className="parent">
                        <label className="planning-group-label">Gender</label>
                        <div className="detail input-wrapper input-inline-align--right planning-group-fields-gender">
                            {profileRequestForm.primaryContact.gender}
                        </div>
                    </div>}
                    {isPrimaryMember === true && <StateOfResidencyInput listOfStates={listOfStates} onSelected={(e: any) => {
                        dispatch(setProfileRequestForm({
                            ...profileRequestForm,
                            primaryContact: {
                                ...profileRequestForm.primaryContact,
                                stateOfResidency: e.itemText
                            }
                        }))
                    }} value={profileRequestForm.primaryContact.stateOfResidency || undefined}/>}
                    {isPrimaryMember === false && <StateOfResidencyInput listOfStates={listOfStates} onSelected={(e: any) => {
                        dispatch(setProfileRequestFormDataForSecondary({
                            ...profileRequestForm,
                            primaryContact: {
                                ...profileRequestForm.primaryContact,
                                stateOfResidency: e.itemText
                            }
                        }))
                    }} value={profileRequestForm.primaryContact.stateOfResidency || undefined}/>}
                    {isPrimaryMember === true && <PersonalPlanningHorizonInput
                        personalPlanningHorizon={profileRequestForm.primaryContact.personalPlanningHorizon}
                        age={profileRequestForm.primaryContact.age}
                        onChange={(pph: number) => dispatch(setProfileRequestForm({
                            ...profileRequestForm,
                            primaryContact: {
                                ...profileRequestForm.primaryContact,
                                personalPlanningHorizon: pph
                            }
                        }))}
                        error={newClientProfileForm?.personalPlanningHorizon}
                        hasInteractions={formInteractions.personalPlanningHorizon}
                        whenUserHasInteracted={() => {
                            dispatch(setNewClientFormInteractions({
                                ...formInteractions,
                                personalPlanningHorizon: true
                            }));
                            onValidateOnBlur?.();
                        }}
                        forceShowErrors={isSaveButtonClicked}/>}
                    {isPrimaryMember === false && <PersonalPlanningHorizonInput
                        personalPlanningHorizon={profileRequestForm.primaryContact.personalPlanningHorizon}
                        age={profileRequestForm.primaryContact.age}
                        onChange={(pph: number) => dispatch(setProfileRequestFormDataForSecondary({
                            ...profileRequestForm,
                            primaryContact: {
                                ...profileRequestForm.primaryContact,
                                personalPlanningHorizon: pph
                            }
                        }))}
                        error={newClientProfileFormForSecondary?.personalPlanningHorizon}
                        hasInteractions={formInteractions.personalPlanningHorizon}
                        whenUserHasInteracted={() => {
                            dispatch(setNewClientFormInteractions({
                                ...formInteractions,
                                personalPlanningHorizon: true
                            }));
                            onValidateOnBlur?.();
                        }}
                        forceShowErrors={isSaveButtonClicked}/>}
                </Col>
            </Row>
        </Grid>
    );
};

